import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Download, ExternalLink, CheckCircle2,
  Sparkles, Target, Users, Mail, BarChart3, Calendar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const PHASES = [
  {
    label: "Phase 1 · Week 1",
    title: "Foundation",
    subtitle: "The unsexy stuff that compounds.",
    icon: CheckCircle2,
    items: [
      {
        title: "Connect to Google Search Console",
        body: "Verify your domain and submit your sitemap (/sitemap.xml). Within 1–2 weeks Google indexes all 23 pages.",
      },
      {
        title: "Optimise your Google Business Profile",
        body: "Add all 5 services, 20+ photos, set the service area to your 20 suburbs, post weekly updates, pre-fill Q&A.",
      },
      {
        title: "Reviews engine — your #1 ranking factor",
        body: "Email/SMS every past client a one-click review link. Aim for 20–30 reviews in 60 days. Use /leave-a-review.",
      },
    ],
  },
  {
    label: "Phase 2 · Week 2–4",
    title: "Paid acquisition",
    subtitle: "Where the new bookings come from.",
    icon: Target,
    items: [
      {
        title: "Google Local Services Ads (LSA)",
        body: "Highest-converting paid channel. Pay per lead ($20–60). Earn the “Google Guaranteed” badge.",
      },
      {
        title: "Google Search Ads ($15–30/day to start)",
        body: "Bid on “bond cleaning brisbane”, “house cleaner [suburb]”, “end of lease cleaning brisbane”. Send each to its matching suburb page for 3–5× higher conversion.",
      },
      {
        title: "Meta (Instagram + Facebook) Ads",
        body: "Women 30–65 in your 20 suburbs · top-25% income · interior design interests. Use short reels of styled homes. $20–30/day.",
      },
    ],
  },
  {
    label: "Phase 3 · Month 2",
    title: "Brand & trust",
    subtitle: "Make people remember you.",
    icon: Users,
    items: [
      {
        title: "Instagram (your premium differentiator)",
        body: "3–4 posts/week. Mix lifestyle, eco-product close-ups, anonymised before/after, tips. Geo-tag every post.",
      },
      {
        title: "Real estate agent partnerships",
        body: "Reach out to 5–10 agencies/week in your suburbs. Presentation cleans + bond cleans = consistent monthly revenue.",
      },
      {
        title: "Email nurture for quote requests",
        body: "Auto-reply, 24-hour follow-up, seasonal newsletter (4× per year). MailerLite is free up to 1,000 subscribers.",
      },
    ],
  },
  {
    label: "Phase 4 · Month 3+",
    title: "Compound growth",
    subtitle: "The long-term flywheel.",
    icon: BarChart3,
    items: [
      {
        title: "Content marketing — one blog/fortnight",
        body: "“Bond cleaning REIQ checklist”, “Best products for Paddington character homes”, “Spring reset for Queensland families”. Each post internal-links to a suburb page.",
      },
      {
        title: "Client referral program",
        body: "Refer a friend, both get 20% off. Once you’re past 50 regular clients, referrals will be 30–40% of new bookings.",
      },
      {
        title: "Direct mail to top suburbs",
        body: "500–1,000 elegant postcards to streets in Ascot, Hamilton, Bulimba, New Farm, Paddington. 2–5% response rate.",
      },
    ],
  },
];

const Playbook = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="90-Day Marketing Playbook | The Sterling Touch"
        description="A prioritised marketing plan for The Sterling Touch — local SEO, Google Ads, partnerships, content and referrals."
        path="/marketing-playbook"
      />
      <header className="sticky top-0 z-40 bg-cream/95 backdrop-blur border-b border-[#e8d9cc]">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-ink hover:text-[#b8866e] transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back to home</span>
          </Link>
          <a
            href="/the-sterling-touch-marketing-playbook.pdf"
            download
          >
            <Button className="rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors">
              <Download size={14} className="mr-2" />
              Download PDF
            </Button>
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-12 bg-white border-b border-[#ead8c9]">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#daae9c]">
              <Sparkles size={14} className="text-sterling-dark" />
              Internal Document
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-ink mt-6 leading-[1.05] text-balance">
              90-Day{" "}
              <span className="italic text-[#daae9c]">Marketing Playbook.</span>
            </h1>
            <p className="mt-6 text-lg text-ink/70 max-w-2xl mx-auto leading-relaxed">
              A prioritised plan for Brisbane growth — ordered by highest ROI
              for least effort, so you always know what to do next.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href="/the-sterling-touch-marketing-playbook.pdf"
                download
              >
                <Button className="rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors h-12 px-6">
                  <Download size={14} className="mr-2" />
                  Download as PDF
                </Button>
              </a>
              <a
                href="/the-sterling-touch-marketing-playbook.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="rounded-full border-ink/20 h-12 px-6">
                  <ExternalLink size={14} className="mr-2" />
                  Open PDF in new tab
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Phases */}
        <section className="py-16 lg:py-20 bg-cream">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 space-y-14">
            {PHASES.map((phase) => {
              const Icon = phase.icon;
              return (
                <div key={phase.label}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#faf3eb] flex items-center justify-center">
                      <Icon size={20} className="text-sterling-dark" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
                        {phase.label}
                      </div>
                      <div className="font-serif-display text-3xl text-ink">
                        {phase.title}
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-ink/70 italic">{phase.subtitle}</p>
                  <div className="mt-6 grid md:grid-cols-3 gap-4">
                    {phase.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-5 bg-white border border-[#ead8c9] rounded-2xl hover:border-sterling-dark transition-colors"
                      >
                        <div className="text-xs uppercase tracking-[0.2em] text-[#daae9c]">
                          Step {idx + 1}
                        </div>
                        <div className="font-serif-display text-lg text-ink mt-2 leading-tight">
                          {item.title}
                        </div>
                        <p className="text-sm text-ink/65 mt-2 leading-relaxed">
                          {item.body}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Budget table */}
        <section className="py-16 bg-white border-y border-[#ead8c9]">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
              Suggested Budget
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink mt-2">
              First 90 days
            </h2>
            <div className="mt-6 grid md:grid-cols-2 gap-3">
              {[
                ["Google LSA", "$400–800 / month", "Highest converting paid channel"],
                ["Google Search Ads", "$400–600 / month", "Catches buying-intent users"],
                ["Meta Ads", "$600–900 / month", "Brand awareness in target areas"],
                ["GBP / Reviews / Email", "Free", "All organic — just your time"],
              ].map(([name, price, why]) => (
                <div
                  key={name}
                  className="p-5 bg-[#faf3eb] rounded-2xl border border-[#ead8c9]"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-serif-display text-lg text-ink">
                      {name}
                    </div>
                    <div className="text-sm text-[#b8866e] whitespace-nowrap">
                      {price}
                    </div>
                  </div>
                  <div className="text-xs text-ink/60 mt-2">{why}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-5 bg-ink text-cream rounded-2xl">
              <div className="text-xs uppercase tracking-[0.25em] text-sterling">
                Total
              </div>
              <div className="font-serif-display text-3xl mt-1">
                ~$1,500 – $2,300 per month
              </div>
              <div className="text-sm text-cream/70 mt-2">
                Aim for a 5–8× return. Pause anything not hitting 4× by month 2.
              </div>
            </div>
          </div>
        </section>

        {/* Recommendation */}
        <section className="py-16 bg-cream">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
              For The Sterling Touch
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink mt-2">
              Where to start tomorrow.
            </h2>
            <ol className="mt-6 space-y-3">
              {[
                "Get to 25+ Google reviews this quarter.",
                "Run Google LSA in your top 5 suburbs only (Ascot, Bulimba, New Farm, Paddington, Ashgrove).",
                "Build 1–2 real estate partnerships before chasing volume.",
                "Post on Instagram weekly — consistency matters more than volume.",
                "Skip Meta ads until reviews and LSA are dialled in.",
              ].map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-4 bg-white border border-[#ead8c9] rounded-xl"
                >
                  <div className="w-7 h-7 rounded-full bg-sterling text-ink flex items-center justify-center font-serif-display text-sm shrink-0">
                    {i + 1}
                  </div>
                  <div className="text-ink leading-relaxed">{t}</div>
                </li>
              ))}
            </ol>

            <div className="mt-10 text-center">
              <a
                href="/the-sterling-touch-marketing-playbook.pdf"
                download
              >
                <Button className="rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors h-12 px-6">
                  <Download size={14} className="mr-2" />
                  Download the full playbook
                </Button>
              </a>
              <p className="mt-3 text-xs text-ink/50">
                Includes outreach email templates and review request copy.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Playbook;
